import React, { useContext, useEffect, useState } from 'react'

import { Spinner } from '@acorns/web-components'
import { useToggle } from '@acorns/web-utils'
import { useMutation, useQuery } from '@apollo/react-hooks'
import * as Sentry from '@sentry/browser'
import { format } from 'date-fns'

import { ErrorContainer } from 'components/ErrorContainer'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { AcceptDocumentResponse, AcceptanceDocument, PageName, User } from 'generated/types'
import trackEvent from 'utils/analytics/trackEvent'
import { FunnelContext } from 'utils/FunnelContext'

import { Banner } from './Banner'
import { Document } from './Document'
import { ReferralDisclosureAgreementScreenViewedEvent } from './events'
import acceptDocumentMutation from './gql/acceptDocument.mutation.gql'
import disclosureQuery from './gql/referralDisclosure.query.gql'
import { SignButton } from './SignButton'
import { SuccessModal } from './SuccessModal'

type UserQueryResponse = {
  user: User
  acceptanceDocument: AcceptanceDocument
}

interface AcceptDocumentInput {
  date: string
  signature: string
  version: string
}

const date = format(new Date(), 'YYYY-MM-DD')

const ReferralDisclosure = () => {
  // User information
  const { loading: isLoadingData, data: queryData } = useQuery<UserQueryResponse>(disclosureQuery, {
    fetchPolicy: 'network-only',
  })
  const { isBackEnabled, isDynamicFunnelEnabled } = useContext(FunnelContext)

  // Document
  const [isDocumentLoading, setIsDocumentLoading] = useToggle(true)

  // Sign button
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const handleButtonClick = () => handleAcceptDocument().then(() => setIsSubmitting(false))
  const [signDisclaimerOffset, setSignDisclaimerOffset] = useState<number>(0)

  // Success modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  // Banner
  const [isBannerLoading, setIsBannerLoading] = useToggle(true)
  const [isDisplayingError, setIsDisplayingError] = useToggle(false)

  // Referral disclosure operations
  const [acceptDocument] = useMutation<AcceptDocumentResponse, AcceptDocumentInput>(
    acceptDocumentMutation,
  )

  const handleAcceptDocument = async () => {
    setIsSubmitting(true)

    try {
      await acceptDocument({
        variables: {
          date,
          signature: `${queryData?.user.firstName ?? ''} ${queryData?.user.lastName ?? ''}`,
          version: queryData?.acceptanceDocument.version,
        },
      })
      setIsModalOpen(true)
    } catch (error) {
      Sentry.captureException(error)
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: ReferralDisclosureAgreementScreenViewedEvent,
    })
  }, [])

  const button = (
    <SignButton
      isSubmitting={isSubmitting}
      isDisabled={isDocumentLoading && isBannerLoading}
      handlePress={handleButtonClick}
      setDisclaimerOffset={setSignDisclaimerOffset}
    />
  )
  const document = (
    <Document
      handleFinishLoading={setIsDocumentLoading}
      documentUrl={queryData?.acceptanceDocument.summaryUrl}
      bottomScrollOffset={signDisclaimerOffset}
    />
  )

  let content
  if (isDisplayingError) {
    content = <ErrorContainer message={'Unable to load referral disclosure'} />
  } else if (isLoadingData) {
    content = <Spinner />
  } else {
    content = (
      <>
        <Banner
          handleFinishLoading={setIsBannerLoading}
          handleError={setIsDisplayingError}
          userSource={queryData?.user.sourceId ?? ''}
        />
        {document}
        {button}
      </>
    )
  }

  return (
    <>
      <Nav
        back={isDynamicFunnelEnabled ? isBackEnabled(PageName.ReferralDisclosure) : true}
        backClickTracker={TRACKER_IDS.REFERRAL_AGREEMENT.BACK_BUTTON}
      />
      <Layout>{content}</Layout>
      <SuccessModal isOpen={isModalOpen} />
    </>
  )
}

export default ReferralDisclosure
